import React, { useState, useEffect, useCallback, memo } from 'react';
import { View, ScrollView, FlatList, TouchableOpacity, Platform, Alert, ActivityIndicator, StyleSheet } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { H2, H3, Text, XStack, YStack, Spinner, Separator, Sheet, Dialog, Adapt, Button as TamaButton, SizableText, H6 } from 'tamagui';
import { MaterialIcons, Ionicons, FontAwesome5 } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import apis from '@api/api';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import globalStyles, { getModernCardStyle } from '@stylesheet';
import moment from 'moment';
import PostDesigner from './Components/PostDesigner';
import { Card } from '@constants/Themed';
import { useIsFreePlanWithContext } from '@utils/billingUtils';
import { checkPostRateLimit, storeLastPostTimestamp, formatRateLimitMessage } from '@utils/postRateLimiter';
import { I18nH6 } from '@components/tamagui';


interface BoostTier {
  type: 'basic' | 'premium' | 'enterprise';
  name: string;
  pricePerDay: number;
  features: string[];
  color: string;
  bgColor: string;
  icon: string;
}

interface ActiveBoost {
  _id: string;
  content: string;
  boost: {
    boostType: string;
    budget: number;
    startDate: string;
    endDate: string;
    status: string;
    metrics: {
      impressions: number;
      clicks: number;
      engagement: number;
      conversions: number;
    };
  };
  createdAt: string;
}

type BoostTierType = 'basic' | 'premium' | 'enterprise';

interface BoostPricingTier {
  name: string;
  pricePerDay: number;
  features: string[];
}

interface BoostPricingResponse {
  pricing?: Record<BoostTierType, BoostPricingTier>;
}

type BoostDuration = 7 | 14 | 30;

interface BuildBoostPayloadArgs {
  postCandidate: any;
  tier: BoostTierType | null;
  duration: BoostDuration | null;
  pricingData: BoostPricingResponse | undefined;
  platform: string;
}

interface BuildBoostPayloadResult {
  postId: string;
  boostType: BoostTierType;
  duration: BoostDuration;
  budget: number;
  platform: string;
}

export const buildBoostPayload = ({
  postCandidate,
  tier,
  duration,
  pricingData,
  platform,
}: BuildBoostPayloadArgs): BuildBoostPayloadResult => {
  if (!tier || !duration) {
    throw new Error('selection-required');
  }

  if (!postCandidate) {
    throw new Error('post-required');
  }

  const postId = postCandidate?._id || postCandidate?.id;
  if (!postId) {
    throw new Error('post-id-missing');
  }

  const tierPricing = pricingData?.pricing?.[tier];
  if (!tierPricing || typeof tierPricing.pricePerDay !== 'number') {
    throw new Error('pricing-unavailable');
  }

  const budget = tierPricing.pricePerDay * duration;

  return {
    postId,
    boostType: tier,
    duration,
    budget,
    platform,
  };
};

/**
 * BoostTierCard Component
 * Displays a boost tier option (Basic, Premium, or Enterprise)
 * with pricing, features, and selection state
 */
const BoostTierCard = memo(({
  tier,
  theme,
  onSelect,
  isSelected
}: {
  tier: BoostTier;
  theme: boolean;
  onSelect: () => void;
  isSelected: boolean;
}) => (
  <Animatable.View
    animation="fadeInUp"
    duration={600}
    style={{
      minWidth: 280,
      maxWidth: 350,
      marginRight: 16,
    }}
  >
    <TouchableOpacity
      onPress={onSelect}
      style={[
        globalStyles.modernCard,
        isSelected ? localStyles.tierCardSelected : (theme ? globalStyles.modernBgLight : globalStyles.modernBgDark),
        {
          borderWidth: isSelected ? 3 : 1,
          borderColor: isSelected ? tier.color : (theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'),
          backgroundColor: isSelected ? tier.bgColor : undefined,
        }
      ]}
    >
      {isSelected && (
        <View style={localStyles.tierCheckmark}>
          <MaterialIcons name="check-circle" size={24} color="white" style={{ backgroundColor: tier.color, borderRadius: 12 }} />
        </View>
      )}

      <View style={[localStyles.tierIconContainer, { backgroundColor: tier.color }]}>
        <MaterialIcons name={tier.icon as any} size={48} color="white" />
      </View>

      <H3 style={{
        color: isSelected ? 'white' : (theme ? '#1e293b' : '#f1f5f9'),
        fontWeight: '800',
        marginBottom: 8,
        textAlign: 'center',
      }}>
        {tier.name}
      </H3>

      <XStack justifyContent="center" alignItems="baseline" marginBottom={16}>
        <Text style={{
          fontSize: 36,
          fontWeight: '800',
          color: tier.color
        }}>
          ${tier.pricePerDay}
        </Text>
        <Text style={{
          fontSize: 14,
          color: theme ? '#64748b' : '#94a3b8',
          marginLeft: 4,
        }}>
          /day
        </Text>
      </XStack>

      <YStack gap={12}>
        {tier.features.map((feature, index) => (
          <XStack key={index} alignItems="center" gap={8}>
            <MaterialIcons
              name="check"
              size={20}
              color={tier.color}
            />
            <Text style={{
              flex: 1,
              fontSize: 13,
              color: isSelected ? 'rgba(255,255,255,0.9)' : (theme ? '#475569' : '#cbd5e1'),
            }}>
              {feature}
            </Text>
          </XStack>
        ))}
      </YStack>
    </TouchableOpacity>
  </Animatable.View>
));
BoostTierCard.displayName = 'BoostTierCard';

const ActiveBoostCard = memo(({
  boost,
  theme,
  onPause,
  onResume,
  onCancel,
  onViewAnalytics
}: {
  boost: ActiveBoost;
  theme: boolean;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onViewAnalytics: () => void;
}) => {
  const isPaused = boost.boost.status === 'paused';
  const isActive = boost.boost.status === 'active';
  const daysLeft = moment(boost.boost.endDate).diff(moment(), 'days');

  return (
    <View style={[
      globalStyles.modernCard,
      theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
      { marginRight: 16, width: 320 }
    ]}>
      <XStack justifyContent="space-between" alignItems="flex-start" marginBottom={12}>
        <YStack flex={1}>
          <Text style={{
            fontSize: 16,
            fontWeight: '700',
            color: theme ? '#1e293b' : '#f1f5f9',
            marginBottom: 4,
          }} numberOfLines={2}>
            {boost.content}
          </Text>
          <Text style={{
            fontSize: 12,
            color: theme ? '#64748b' : '#94a3b8',
          }}>
            {boost.boost.boostType.toUpperCase()} • {daysLeft} days left
          </Text>
        </YStack>
        <View style={{
          backgroundColor: isPaused ? '#ff9800' : (isActive ? '#4caf50' : '#9e9e9e'),
          paddingHorizontal: 12,
          paddingVertical: 6,
          borderRadius: 12,
        }}>
          <Text style={{ color: 'white', fontSize: 11, fontWeight: '600' }}>
            {boost.boost.status.toUpperCase()}
          </Text>
        </View>
      </XStack>

      {/* Metrics */}
      <XStack gap={8} flexWrap="wrap" marginBottom={16}>
        {[
          { label: 'Impressions', value: boost.boost.metrics.impressions, icon: 'eye' },
          { label: 'Clicks', value: boost.boost.metrics.clicks, icon: 'hand-pointer-o' },
          { label: 'Engagement', value: boost.boost.metrics.engagement, icon: 'heart' },
          { label: 'Conversions', value: boost.boost.metrics.conversions, icon: 'check-circle' },
        ].map((metric, index) => (
          <View key={index} style={[
            globalStyles.modernCardSmall,
            theme ? globalStyles.modernBgLightSubtle : globalStyles.modernBgDarkSubtle,
            localStyles.metricCard
          ]}>
            <FontAwesome5 name={metric.icon} size={16} color={theme ? '#64748b' : '#94a3b8'} />
            <Text style={{
              fontSize: 18,
              fontWeight: '700',
              color: theme ? '#1e293b' : '#f1f5f9',
              marginTop: 4,
            }}>
              {metric.value.toLocaleString()}
            </Text>
            <Text style={{
              fontSize: 10,
              color: theme ? '#94a3b8' : '#64748b',
            }}>
              {metric.label}
            </Text>
          </View>
        ))}
      </XStack>

      {/* Actions */}
      <XStack gap={8}>
        <TouchableOpacity
          onPress={onViewAnalytics}
          style={[
            globalStyles.modernActionButton,
            theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark,
            { flex: 1, paddingHorizontal: 12, paddingVertical: 12 }
          ]}
        >
          <Text style={{ color: 'white', fontWeight: '600' }}>
            Analytics
          </Text>
        </TouchableOpacity>

        {isActive && (
          <TouchableOpacity
            onPress={onPause}
            style={[
              globalStyles.modernActionButton,
              theme ? globalStyles.modernActionAmber : globalStyles.modernActionAmberDark,
              { flex: 1, paddingHorizontal: 12, paddingVertical: 12 }
            ]}
          >
            <Text style={{ color: 'white', fontWeight: '600' }}>
              Pause
            </Text>
          </TouchableOpacity>
        )}

        {isPaused && (
          <TouchableOpacity
            onPress={onResume}
            style={[
              globalStyles.modernActionButton,
              theme ? globalStyles.modernActionGreen : globalStyles.modernActionGreenDark,
              { flex: 1, paddingHorizontal: 12, paddingVertical: 12 }
            ]}
          >
            <Text style={{ color: 'white', fontWeight: '600' }}>
              Resume
            </Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          onPress={onCancel}
          style={[
            globalStyles.modernActionButton,
            theme ? globalStyles.modernActionRed : globalStyles.modernActionRedDark,
            { flex: 1, paddingHorizontal: 12, paddingVertical: 12 }
          ]}
        >
          <Text style={{ color: 'white', fontWeight: '600' }}>
            Cancel
          </Text>
        </TouchableOpacity>
      </XStack>
    </View>
  );
});
ActiveBoostCard.displayName = 'ActiveBoostCard';

/**
 * BoostPostScreen Component
 * Main screen for managing post boosts with react-native-iap integration
 * Features:
 * - View and manage active boosts
 * - Purchase new boosts with in-app purchases
 * - Select posts, tiers, and duration
 * - Track boost analytics
 */
const BoostPostScreen = () => {
  const theme = themeSelector();
  const queryClient = useQueryClient();
  const isFreePlan = useIsFreePlanWithContext();
  const [step, setStep] = useState<'select_boost' | 'select_content' | 'create_content'>('select_boost');
  const [selectedTier, setSelectedTier] = useState<BoostTierType | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<BoostDuration | null>(null);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [newPostData, setNewPostData] = useState<any>(null);

  // Fetch boost pricing from backend
  const { data: pricingData, isLoading: pricingLoading } = useQuery({
    queryKey: ['boostPricing'],
    queryFn: async () => await apis.getBoostPricing(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch user's posts
  const { data: userPosts, isLoading: postsLoading } = useQuery({
    queryKey: ['userPosts'],
    queryFn: async () => await apis.getUserPosts(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch active boosts
  const { data: activeBoosts, isLoading: boostsLoading, refetch: refetchBoosts } = useQuery({
    queryKey: ['activeBoosts'],
    queryFn: async () => await apis.getAllUserBoosts(),
    staleTime: 2 * 60 * 1000,
  });

  // Create boost mutation
  const createBoostMutation = useMutation({
    mutationFn: async (boostData: any) => {
      return await apis.createPostBoost(boostData.postId, boostData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeBoosts'] });
    },
  });

  // Pause boost mutation
  const pauseBoostMutation = useMutation({
    mutationFn: async (postId: string) => await apis.pausePostBoost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeBoosts'] });
    },
  });

  // Resume boost mutation
  const resumeBoostMutation = useMutation({
    mutationFn: async (postId: string) => await apis.resumePostBoost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeBoosts'] });
    },
  });

  // Cancel boost mutation
  const cancelBoostMutation = useMutation({
    mutationFn: async (postId: string) => await apis.cancelPostBoost(postId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['activeBoosts'] });
      if (data?.refund) {
        Alert.alert(
          'Boost Cancelled',
          `Refund of $${data.refund.amount.toFixed(2)} will be processed within 5-7 business days.`,
        );
      }
    },
  });

  const handleTierSelection = useCallback((tier: BoostTierType) => {
    setSelectedTier(tier);
  }, []);

  const handleDurationSelection = useCallback((duration: BoostDuration) => {
    setSelectedDuration(duration);
  }, []);

  const handleContinueToContent = useCallback(() => {
    if (!selectedTier || !selectedDuration) {
      Alert.alert('Selection Required', 'Please select a boost tier and duration first.');
      return;
    }
    setStep('select_content');
  }, [selectedTier, selectedDuration]);

  const handleCreateNewPost = useCallback(() => {
    setStep('create_content');
    setSelectedPost(null);
    setNewPostData(null);
  }, []);

  const handleSelectExisting = useCallback(() => {
    // Just a placeholder - user is already in select_content step
    // They can select from the list below
  }, []);

  const handleBackToBoostSelection = useCallback(() => {
    setStep('select_boost');
  }, []);

  const handlePostSave = useCallback(async (postData: any) => {
    try {
      // Check rate limit before posting
      const rateLimitResult = await checkPostRateLimit(isFreePlan);
      if (!rateLimitResult.allowed) {
        const errorMessage = formatRateLimitMessage(rateLimitResult, isFreePlan);
        Alert.alert('Rate Limit', errorMessage);
        return;
      }

      // Save post to feed
      const result = await apis.postToFeed({
        content: `${postData.headline}\n\n${postData.caption}`,
        hashtags: postData.hashtags,
        location: postData.location,
        media: postData.image ? [postData.image] : [],
      });

      // Store last post timestamp after successful post
      await storeLastPostTimestamp();

      setNewPostData(result);
      setSelectedPost(result.data);
      setStep('select_content'); // Go back to overview to confirm

      Alert.alert('Success! 🎉', 'Post created and ready to boost!');
    } catch (error) {
      console.error('Error creating post:', error);
      Alert.alert('Error', 'Failed to create post. Please try again.');
    }
  }, [isFreePlan]);

  const handlePostPublish = useCallback(async (postData: any) => {
    // When publishing from designer, save and prepare for boost
    await handlePostSave(postData);
  }, [handlePostSave]);

  const handlePurchaseBoost = useCallback(async () => {
    const postCandidate = selectedPost || newPostData?.data || newPostData;

    try {
      const payload = buildBoostPayload({
        postCandidate,
        tier: selectedTier,
        duration: selectedDuration,
        pricingData,
        platform: Platform.OS,
      });

      setIsPurchasing(true);

      await createBoostMutation.mutateAsync(payload);

      Alert.alert(
        'Boost Activated! 🚀',
        `Your ${payload.boostType} boost is now active and will run for ${payload.duration} days.`,
        [{ text: 'Great!', style: 'default' }]
      );

      setSelectedPost(null);
      setSelectedTier(null);
      setSelectedDuration(null);
      setStep('select_boost');
      refetchBoosts();
    } catch (error) {
      console.error('Error activating boost:', error);

      const message = error instanceof Error ? error.message : 'boost-failed';

      switch (message) {
        case 'selection-required':
          Alert.alert('Selection Required', 'Please choose a boost tier and duration first.');
          break;
        case 'post-required':
          Alert.alert('Select a Post', 'Please select or create a post to boost first.');
          break;
        case 'post-id-missing':
          Alert.alert('Unable to Boost', 'The selected post is missing an identifier. Please try again.');
          break;
        case 'pricing-unavailable':
          Alert.alert('Pricing Unavailable', 'Boost pricing data is currently unavailable. Please try again later.');
          break;
        default:
          Alert.alert('Boost Failed', 'We were unable to start the boost. Please try again or contact support.');
          break;
      }
    } finally {
      setIsPurchasing(false);
    }
  }, [selectedPost, newPostData, selectedTier, selectedDuration, pricingData, createBoostMutation, refetchBoosts]);

  const boostTiers: BoostTier[] = pricingData?.pricing ? [
    {
      type: 'basic',
      name: pricingData.pricing.basic.name,
      pricePerDay: pricingData.pricing.basic.pricePerDay,
      features: pricingData.pricing.basic.features,
      color: '#2196f3',
      bgColor: '#1976d2',
      icon: 'rocket-launch',
    },
    {
      type: 'premium',
      name: pricingData.pricing.premium.name,
      pricePerDay: pricingData.pricing.premium.pricePerDay,
      features: pricingData.pricing.premium.features,
      color: '#9c27b0',
      bgColor: '#7b1fa2',
      icon: 'auto-awesome',
    },
    {
      type: 'enterprise',
      name: pricingData.pricing.enterprise.name,
      pricePerDay: pricingData.pricing.enterprise.pricePerDay,
      features: pricingData.pricing.enterprise.features,
      color: '#ff9800',
      bgColor: '#f57c00',
      icon: 'workspace-premium',
    },
  ] : [];

  const ModernSectionHeader = useCallback(({ title, subtitle, icon }: { title: string; subtitle?: string; icon: string }) => (
    <View style={[
      globalStyles.modernCard,
      theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
      { marginBottom: 16 }
    ]}>
      <XStack alignItems="center" gap={16}>
        <View style={[
          localStyles.sectionIconContainer,
          { backgroundColor: theme ? '#e3f2fd' : '#1565c0' }
        ]}>
          <MaterialIcons name={icon as any} size={32} color={theme ? '#1976d2' : '#64b5f6'} />
        </View>
        <YStack flex={1}>
          <Text style={{
            color: theme ? '#1e293b' : '#f1f5f9',
            fontWeight: '800',
            fontSize: 20,
          }}>
            {title}
          </Text>
          {subtitle && (
            <Text style={{
              color: theme ? '#64748b' : '#94a3b8',
              fontSize: 14,
              marginTop: 4,
            }}>
              {subtitle}
            </Text>
          )}
        </YStack>
      </XStack>
    </View>
  ), [theme]);

  if (pricingLoading || postsLoading) {
    return (
      <DefaultScreen backButton style={globalStyles.modernMainContent}>
        <View style={[
          globalStyles.modernCard,
          theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
          { alignItems: 'center', padding: 24, marginTop: 20 }
        ]}>
          <View style={{ alignItems: 'center', gap: 16 }}>
            <Spinner size="large" color="$blue10" />
            <Text style={{
              color: theme ? '#1e293b' : '#f1f5f9',
              fontSize: 16,
              fontWeight: '500'
            }}>
              Loading boost options...
            </Text>
          </View>
        </View>
      </DefaultScreen>
    );
  }

  // Render PostDesigner step
  if (step === 'create_content') {
    return (
      <DefaultScreen backButton={false} style={globalStyles.modernMainContent}>
        <YStack flex={1} gap="$4">
          {/* Header with back button */}
          <View style={[
            globalStyles.modernCard,
            theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
          ]}>
            <XStack alignItems="center" gap={16}>
              <TouchableOpacity
                onPress={() => setStep('select_content')}
                style={[
                  globalStyles.modernActionButton,
                  theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark,
                ]}
              >
                <MaterialIcons name="arrow-back" size={24} color="white" />
              </TouchableOpacity>
              <YStack flex={1}>
                <Text style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontWeight: '800',
                  fontSize: 20,
                }}>
                  Design Your Boost
                </Text>
                <Text style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 14,
                }}>
                  {selectedTier?.toUpperCase()} • {selectedDuration} days • ${pricingData?.pricing?.[selectedTier]?.pricePerDay * (selectedDuration || 7)}
                </Text>
              </YStack>
              <View style={{
                backgroundColor: theme ? '#fff3e0' : '#e65100',
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 12,
              }}>
                <Text style={{ color: theme ? '#f57c00' : '#ffcc02', fontWeight: '600', fontSize: 12 }}>
                  AI Enhanced
                </Text>
              </View>
            </XStack>
          </View>

          {/* PostDesigner */}
          <View style={{ flex: 1 }}>
            <PostDesigner
              initialData={null}
              onSave={handlePostSave}
              onPublish={handlePostPublish}
            />
          </View>
        </YStack>
      </DefaultScreen>
    );
  }

  return (
    <DefaultScreen backButton>
      <XStack overflow="scroll" flexWrap={Platform.OS !== 'web' ? "wrap" : "nowrap"} flexDirection={Platform.OS === 'web' ? 'row' : 'column'} gap={20} flex={1}>
        {/* Main Content - Left Side */}
        <YStack flex={Platform.OS === 'web' ? 2 : 1} gap="$4" paddingBottom={Platform.OS !== 'web' ? 120 : 100}>
          {/* Header */}
          <Animatable.View animation="fadeInDown" duration={800}>
            <ModernSectionHeader
              title={step === 'select_boost' ? 'Choose Your Boost' : 'Select Your Content'}
              subtitle={step === 'select_boost'
                ? 'Pick the perfect boost package for your needs'
                : 'Create a new post or boost an existing one'}
              icon={step === 'select_boost' ? 'rocket-launch' : 'photo-library'}
            />
          </Animatable.View>

          {/* Active Boosts - Show on all steps */}
          {activeBoosts?.boosts && activeBoosts.boosts.length > 0 && (
            <Animatable.View animation="fadeIn" style={{ gap: 12 }}>
              <View style={localStyles.sectionTitleContainer}>
                <Text style={[
                  globalStyles.modernSectionTitle,
                  { color: theme ? '#1e293b' : '#f1f5f9' }
                ]}>
                  Active Boosts
                </Text>
                <Text style={{
                  fontSize: 12,
                  color: theme ? '#64748b' : '#94a3b8',
                }}>
                  {activeBoosts.boosts.length} active
                </Text>
              </View>
              <FlatList
                data={activeBoosts.boosts}
                horizontal
                showsHorizontalScrollIndicator={false}
                keyExtractor={(item) => item._id}
                contentContainerStyle={{ paddingHorizontal: 20 }}
                renderItem={({ item }) => (
                  <ActiveBoostCard
                    boost={item}
                    theme={theme}
                    onPause={() => pauseBoostMutation.mutate(item._id)}
                    onResume={() => resumeBoostMutation.mutate(item._id)}
                    onCancel={() => {
                      Alert.alert(
                        'Cancel Boost?',
                        'You will receive a prorated refund. Are you sure?',
                        [
                          { text: 'No', style: 'cancel' },
                          { text: 'Yes, Cancel', style: 'destructive', onPress: () => cancelBoostMutation.mutate(item._id) },
                        ]
                      );
                    }}
                    onViewAnalytics={() => {
                      // Navigate to analytics screen or show modal
                      console.log('View analytics for', item._id);
                    }}
                  />
                )}
              />
            </Animatable.View>
          )}

          {/* Step 1: Boost Selection */}
          {step === 'select_boost' && (
            <>
              {/* Boost Tiers */}
              <Animatable.View animation="fadeIn" style={{ gap: 12 }}>
                <View style={localStyles.sectionTitleContainer}>
                  <Text style={[
                    globalStyles.modernSectionTitle,
                    { color: theme ? '#1e293b' : '#f1f5f9' }
                  ]}>
                    Choose Your Boost Tier
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    color: theme ? '#64748b' : '#94a3b8',
                    marginTop: 4,
                  }}>
                    {selectedTier ? `${selectedTier.toUpperCase()} selected` : 'Select a tier'}
                  </Text>
                </View>
                <View style={[getModernCardStyle(theme), { padding: 20 }]}>
                  <I18nH6 style={{ textAlign: 'center' }}>Post boosting coming soon! </I18nH6>
                </View>
                <FlatList
                  data={boostTiers}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  keyExtractor={(item) => item.type}
                  contentContainerStyle={{ paddingHorizontal: 20 }}
                  renderItem={({ item }) => (
                    <BoostTierCard
                      tier={item}
                      theme={theme}
                      onSelect={() => handleTierSelection(item.type)}
                      isSelected={selectedTier === item.type}
                    />
                  )}
                />
              </Animatable.View>

              {/* Duration Selection */}
              <Animatable.View animation="fadeIn" style={{ gap: 12 }}>
                <View style={localStyles.sectionTitleContainer}>
                  <Text style={[
                    globalStyles.modernSectionTitle,
                    { color: theme ? '#1e293b' : '#f1f5f9' }
                  ]}>
                    Select Duration
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    color: theme ? '#64748b' : '#94a3b8',
                    marginTop: 4,
                  }}>
                    {selectedDuration ? `${selectedDuration} days selected` : 'Choose duration'}
                  </Text>
                </View>
                <XStack gap={12} justifyContent="center" paddingHorizontal={20}>
                  {[7, 14, 30].map((days) => (
                    <TouchableOpacity
                      key={days}
                      onPress={() => handleDurationSelection(days as 7 | 14 | 30)}
                      style={[
                        globalStyles.modernCardSmall,
                        selectedDuration === days
                          ? { backgroundColor: boostTiers.find(t => t.type === selectedTier)?.color || '#2196f3' }
                          : (theme ? globalStyles.modernBgLight : globalStyles.modernBgDark),
                        localStyles.durationCard
                      ]}
                    >
                      <Text style={{
                        fontSize: 24,
                        fontWeight: '800',
                        color: selectedDuration === days ? 'white' : (theme ? '#1e293b' : '#f1f5f9'),
                      }}>
                        {days}
                      </Text>
                      <Text style={{
                        fontSize: 12,
                        color: selectedDuration === days ? 'rgba(255,255,255,0.9)' : (theme ? '#64748b' : '#94a3b8'),
                        marginTop: 4,
                      }}>
                        days
                      </Text>
                      {pricingData?.pricing && selectedTier && (
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '700',
                          color: selectedDuration === days ? 'white' : (theme ? '#1e293b' : '#f1f5f9'),
                          marginTop: 8,
                        }}>
                          ${(pricingData.pricing[selectedTier].pricePerDay * days).toFixed(0)}
                        </Text>
                      )}
                    </TouchableOpacity>
                  ))}
                </XStack>
              </Animatable.View>

            </>
          )}

          {/* Step 2: Content Selection */}
          {step === 'select_content' && (
            <>
              {/* Back Button */}
              <Animatable.View animation="fadeIn">
                <TouchableOpacity
                  onPress={handleBackToBoostSelection}
                  style={[
                    globalStyles.modernActionButton,
                    theme ? globalStyles.modernActiongrey : globalStyles.modernActiongreyDark,
                    { marginLeft: 20, alignSelf: 'flex-start' }
                  ]}
                >
                  <XStack alignItems="center" gap={8}>
                    <MaterialIcons name="arrow-back" size={20} color="white" />
                    <Text style={{ color: 'white', fontWeight: '600' }}>Back</Text>
                  </XStack>
                </TouchableOpacity>
              </Animatable.View>

              {/* Selected Boost Summary */}
              <Animatable.View animation="fadeIn">
                <View style={[
                  globalStyles.modernCard,
                  theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
                  {
                    borderLeftWidth: 4,
                    borderLeftColor: boostTiers.find(t => t.type === selectedTier)?.color || '#2196f3',
                  }
                ]}>
                  <XStack alignItems="center" justifyContent="space-between">
                    <YStack flex={1}>
                      <Text style={{
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontWeight: '700',
                        fontSize: 16,
                      }}>
                        {boostTiers.find(t => t.type === selectedTier)?.name}
                      </Text>
                      <Text style={{
                        color: theme ? '#64748b' : '#94a3b8',
                        fontSize: 14,
                        marginTop: 4,
                      }}>
                        {selectedDuration} days • ${pricingData?.pricing?.[selectedTier]?.pricePerDay * (selectedDuration || 7)} total
                      </Text>
                    </YStack>
                    <MaterialIcons
                      name="verified"
                      size={32}
                      color={boostTiers.find(t => t.type === selectedTier)?.color}
                    />
                  </XStack>
                </View>
              </Animatable.View>

              {/* AI Tip Section */}
              <Animatable.View animation="fadeIn" style={{ gap: 12 }}>
                <View style={[
                  globalStyles.modernCardSmall,
                  theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
                  {
                    borderLeftWidth: 4,
                    borderLeftColor: '#ff9800',
                    backgroundColor: theme ? '#fff3e0' : '#e65100',
                  }
                ]}>
                  <XStack alignItems="center" gap={12}>
                    <MaterialIcons name="lightbulb" size={24} color={theme ? '#f57c00' : '#ffcc02'} />
                    <YStack flex={1}>
                      <Text style={{
                        color: theme ? '#e65100' : '#ffcc02',
                        fontWeight: '700',
                        fontSize: 14,
                        marginBottom: 4,
                      }}>
                        AI-Powered Content
                      </Text>
                      <Text style={{
                        color: theme ? '#f57c00' : '#ffb74d',
                        fontSize: 12,
                        lineHeight: 18,
                      }}>
                        Create optimized content with AI for your {selectedTier} boost to maximize engagement
                      </Text>
                    </YStack>
                  </XStack>
                </View>
              </Animatable.View>

              {/* Create or Select Options */}
              <Animatable.View animation="fadeIn" style={{ gap: 12 }}>
                <View style={localStyles.sectionTitleContainer}>
                  <Text style={[
                    globalStyles.modernSectionTitle,
                    { color: theme ? '#1e293b' : '#f1f5f9' }
                  ]}>
                    Choose Your Content
                  </Text>
                </View>
                <XStack gap={12} justifyContent="center" paddingHorizontal={20}>
                  <TouchableOpacity
                    onPress={handleCreateNewPost}
                    style={[
                      globalStyles.modernCard,
                      theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
                      { flex: 1, alignItems: 'center', padding: 24 }
                    ]}
                  >
                    <MaterialIcons
                      name="auto-awesome"
                      size={48}
                      color={theme ? '#2196f3' : '#64b5f6'}
                    />
                    <Text style={{
                      color: theme ? '#1e293b' : '#f1f5f9',
                      fontWeight: '700',
                      marginTop: 12,
                      fontSize: 16,
                    }}>
                      Create with AI
                    </Text>
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 12,
                      marginTop: 4,
                      textAlign: 'center',
                    }}>
                      Design optimized content
                    </Text>
                  </TouchableOpacity>

                  <View
                    style={[
                      globalStyles.modernCard,
                      theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
                      { flex: 1, alignItems: 'center', padding: 24, opacity: (!userPosts || userPosts.length === 0) ? 0.5 : 1 }
                    ]}
                  >
                    <MaterialIcons
                      name="photo-library"
                      size={48}
                      color={theme ? '#9c27b0' : '#ce93d8'}
                    />
                    <Text style={{
                      color: theme ? '#1e293b' : '#f1f5f9',
                      fontWeight: '700',
                      marginTop: 12,
                      fontSize: 16,
                    }}>
                      Select Existing
                    </Text>
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 12,
                      marginTop: 4,
                      textAlign: 'center',
                    }}>
                      {!userPosts || userPosts.length === 0 ? 'No posts available' : `From your ${userPosts.length} posts below`}
                    </Text>
                  </View>
                </XStack>
              </Animatable.View>

              {/* Select Existing Post List */}
              <Animatable.View animation="fadeIn" style={{ gap: 12 }}>
                <View style={localStyles.sectionTitleContainer}>
                  <Text style={[
                    globalStyles.modernSectionTitle,
                    { color: theme ? '#1e293b' : '#f1f5f9' }
                  ]}>
                    Your Recent Posts
                  </Text>
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={globalStyles.modernHorizontalList}>
                  <XStack gap={12}>
                    {userPosts?.slice(0, 5).map((post: any) => (
                      <TouchableOpacity
                        key={post._id || post.id}
                        onPress={() => {
                          setSelectedPost(post);
                          setNewPostData(null);
                        }}
                        style={[
                          globalStyles.modernListCard,
                          selectedPost?._id === post._id
                            ? {
                              backgroundColor: boostTiers.find(t => t.type === selectedTier)?.bgColor,
                              borderWidth: 3,
                              borderColor: boostTiers.find(t => t.type === selectedTier)?.color
                            }
                            : (theme ? globalStyles.modernBgLight : globalStyles.modernBgDark),
                          localStyles.postCard
                        ]}
                      >
                        <View style={{ padding: 12 }}>
                          <Text
                            numberOfLines={3}
                            style={{
                              color: selectedPost?._id === post._id ? 'white' : (theme ? '#1e293b' : '#f1f5f9'),
                              fontSize: 14,
                              fontWeight: '600',
                            }}
                          >
                            {post.content}
                          </Text>
                          <Text style={{
                            color: selectedPost?._id === post._id ? 'rgba(255,255,255,0.8)' : (theme ? '#64748b' : '#94a3b8'),
                            fontSize: 12,
                            marginTop: 8,
                          }}>
                            {moment(post.createdAt).format('MMM DD, YYYY')}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </XStack>
                </ScrollView>
              </Animatable.View>

            </>
          )}
        </YStack>
      </XStack>

      {/* Fixed Bottom Button - Step 1: Continue */}
      {step === 'select_boost' && Platform.OS !== 'web' && (
        <Animatable.View
          animation="slideInUp"
          style={[
            localStyles.fixedBottomButton,
            getModernCardStyle(theme),
            {
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderTopColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            }
          ]}
        >
          <TouchableOpacity
            onPress={handleContinueToContent}
            disabled={!selectedTier || !selectedDuration}
            style={[
              globalStyles.modernButton,
              {
                backgroundColor: (!selectedTier || !selectedDuration)
                  ? (theme ? '#e2e8f0' : '#334155')
                  : '#2196f3',
                shadowColor: '#2196f3',
                shadowOpacity: (!selectedTier || !selectedDuration) ? 0 : 0.3,
                marginHorizontal: 20,
                marginBottom: 20,
              }
            ]}
          >
            <XStack alignItems="center" gap={8}>
              <Text style={globalStyles.appButtonText}>
                Continue to Content Selection
              </Text>
              <MaterialIcons name="arrow-forward" size={20} color="white" />
            </XStack>
            {pricingData?.pricing && selectedTier && selectedDuration && (
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginTop: 4 }}>
                Total: ${(pricingData.pricing[selectedTier].pricePerDay * selectedDuration).toFixed(2)}
              </Text>
            )}
          </TouchableOpacity>
        </Animatable.View>
      )}

      {/* Fixed Bottom Button - Step 2: Purchase Boost */}
      {step === 'select_content' && Platform.OS !== 'web' && (
        <Animatable.View
          animation="slideInUp"
          style={[
            localStyles.fixedBottomButton,
            {
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderTopColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            }
          ]}
        >
          <TouchableOpacity
            onPress={handlePurchaseBoost}
            disabled={(!selectedPost && !newPostData) || isPurchasing}
            style={[
              globalStyles.modernButton,
              {
                backgroundColor: (!selectedPost && !newPostData) || isPurchasing
                  ? (theme ? '#e2e8f0' : '#334155')
                  : boostTiers.find(t => t.type === selectedTier)?.color,
                shadowColor: boostTiers.find(t => t.type === selectedTier)?.color,
                shadowOpacity: (!selectedPost && !newPostData) || isPurchasing ? 0 : 0.3,
                marginHorizontal: 20,
                marginBottom: 20,
              }
            ]}
          >
            {isPurchasing ? (
              <Spinner color="white" />
            ) : (
              <>
                <XStack alignItems="center" gap={8}>
                  <MaterialIcons name="rocket-launch" size={24} color="white" />
                  <Text style={globalStyles.appButtonText}>
                    Purchase Boost Now
                  </Text>
                </XStack>
                {pricingData?.pricing && selectedTier && selectedDuration && (
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginTop: 4 }}>
                    ${(pricingData.pricing[selectedTier].pricePerDay * selectedDuration).toFixed(2)} for {selectedDuration} days
                  </Text>
                )}
              </>
            )}
          </TouchableOpacity>
        </Animatable.View>
      )}
    </DefaultScreen>
  );
};

const localStyles = StyleSheet.create({
  tierCardSelected: {
    transform: [{ scale: 1.02 }],
  },
  tierCheckmark: {
    position: 'absolute',
    top: 12,
    right: 12,
    borderRadius: 20,
    padding: 8,
    zIndex: 10,
  },
  tierIconContainer: {
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    marginBottom: 16,
  },
  metricCard: {
    flex: 1,
    minWidth: 70,
    alignItems: 'center',
  },
  sectionTitleContainer: {
    paddingHorizontal: 20,
    marginBottom: 8,
  },
  sectionIconContainer: {
    borderRadius: 16,
    padding: 16,
  },
  durationCard: {
    flex: 1,
    borderWidth: 2,
    alignItems: 'center',
  },
  postCard: {
    width: 200,
    overflow: 'hidden',
  },
  boostButton: {
    marginHorizontal: 20,
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fixedBottomButton: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingTop: 12,
    paddingBottom: Platform.OS === 'ios' ? 34 : 20, // Account for iOS safe area
    borderTopWidth: 1,
    zIndex: 1000,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
      },
      android: {
      },
      web: {
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
      },
    }),
  },
});

// Wrap with IAP Context
export default BoostPostScreen;

